import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export type PenName = {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  website: string | null;
  social_links: Record<string, string>;
  created_at: string;
};

export function usePenNames() {
  const [penNames, setPenNames] = useState<PenName[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchPenNames();
    }
  }, [user]);

  const fetchPenNames = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('pen_names')
        .select('*')
        .eq('owner_id', user!.id)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;
      setPenNames(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch pen names');
    } finally {
      setLoading(false);
    }
  };

  const createPenName = async (penName: Omit<PenName, 'id' | 'created_at'>) => {
    try {
      const { error: createError } = await supabase
        .from('pen_names')
        .insert({
          ...penName,
          owner_id: user!.id
        });

      if (createError) throw createError;
      await fetchPenNames();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to create pen name');
    }
  };

  const updatePenName = async (id: string, updates: Partial<Omit<PenName, 'id' | 'created_at' | 'username'>>) => {
    try {
      const { error: updateError } = await supabase
        .from('pen_names')
        .update(updates)
        .eq('id', id)
        .eq('owner_id', user!.id);

      if (updateError) throw updateError;
      await fetchPenNames();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to update pen name');
    }
  };

  const deletePenName = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('pen_names')
        .delete()
        .eq('id', id)
        .eq('owner_id', user!.id);

      if (deleteError) throw deleteError;
      await fetchPenNames();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to delete pen name');
    }
  };

  const switchToPenName = async (id: string) => {
    try {
      // Store the current pen name ID in localStorage
      localStorage.setItem('current_identity_id', id);
      
      // Refresh the page to apply the change
      window.location.reload();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to switch pen name');
    }
  };

  return {
    penNames,
    loading,
    error,
    createPenName,
    updatePenName,
    deletePenName,
    switchToPenName
  };
}